<template>
  <layout-header />
  <layout-sidebar />

  <div class="page-wrapper">
    <div class="content">
      <div
        class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3"
      >
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div class="d-flex gap-2 flex-wrap">
          <button
            type="button"
            class="btn btn-outline-primary"
            v-can="'Payroll.Edit'"
            @click="startCorporateStarter"
          >
            Start from a corporate bank template
          </button>
          <button
            type="button"
            class="btn btn-primary"
            v-can="'Payroll.Edit'"
            @click="startCreate"
          >
            <i class="ti ti-plus me-1"></i>New bank format
          </button>
        </div>
      </div>

      <p class="dq-bt-lead">
        Tell DoneQuick which payroll detail goes in each column of your bank’s upload file.
        You set this up once. Each month, export from <strong>Bank Transfer Report</strong> using this format.
      </p>

      <div class="row g-3">
        <div class="col-lg-4">
          <div class="card dq-bt-card h-100">
            <div class="card-header dq-bt-card__head">
              <div>
                <h5 class="mb-0">Your bank formats</h5>
                <p class="dq-bt-card__sub mb-0">Pick one to review or edit</p>
              </div>
            </div>
            <ul class="list-group list-group-flush">
              <li
                class="list-group-item list-group-item-action dq-template-item"
                :class="{ active: selectedId === 0 }"
                @click="selectLegacy"
              >
                <div class="d-flex justify-content-between align-items-start gap-2">
                  <div>
                    <div class="fw-semibold dq-template-item__title">Standard (DoneQuick)</div>
                    <div class="small dq-template-item__meta">Ready-made file. No setup needed.</div>
                  </div>
                  <span class="badge dq-bt-badge">Built-in</span>
                </div>
              </li>
              <li
                v-for="t in templates"
                :key="t.id"
                class="list-group-item list-group-item-action dq-template-item"
                :class="{ active: selectedId === t.id }"
                @click="selectTemplate(t)"
              >
                <div class="d-flex justify-content-between align-items-start gap-2">
                  <div>
                    <div class="fw-semibold">{{ t.name }}</div>
                    <div class="small dq-template-item__meta">
                      {{ (t.file_format || "csv").toUpperCase() }}
                      · {{ mappedColumnCount(t) }} columns filled
                      <span v-if="t.filter_bank_code"> · bank {{ t.filter_bank_code }}</span>
                    </div>
                  </div>
                  <span v-if="t.is_default" class="badge bg-success">Default</span>
                </div>
              </li>
              <li
                v-if="!templates.length"
                class="list-group-item text-muted small"
              >
                No custom formats yet. Start with <strong>New bank format</strong> or <strong>Corporate PAY starter</strong>.
              </li>
            </ul>
          </div>
        </div>

        <div class="col-lg-8">
          <div class="card dq-bt-card">
            <div class="card-header dq-bt-card__head d-flex justify-content-between align-items-center gap-2">
              <div>
                <h5 class="mb-0">{{ editorTitle }}</h5>
                <p class="dq-bt-card__sub mb-0">{{ editorSubtitle }}</p>
              </div>
              <div v-if="selectedId > 0" class="d-flex gap-2">
                <button
                  type="button"
                  class="btn btn-sm btn-outline-secondary"
                  v-can="'Payroll.Edit'"
                  @click="duplicateTemplate"
                >
                  Duplicate
                </button>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-danger"
                  v-can="'Payroll.Edit'"
                  @click="openRemoveTemplateModal"
                >
                  Delete
                </button>
              </div>
            </div>
            <div class="card-body">
              <div v-if="selectedId === 0" class="dq-bt-standard">
                <p class="dq-standard-desc mb-2">
                  This is the built-in file used when you choose <strong>Standard (DoneQuick)</strong> on the Bank Transfer Report.
                  It already includes employee name, bank, account, and net pay.
                </p>
                <p class="small text-muted mb-3">
                  Use a custom format only when your bank gave you its own spreadsheet layout.
                </p>
                <button
                  type="button"
                  class="btn btn-outline-primary btn-sm"
                  v-can="'Payroll.Edit'"
                  @click="startCreate"
                >
                  Copy this layout and customise it
                </button>
              </div>

              <form v-else @submit.prevent="save">
                <div class="dq-bt-section">
                  <div class="dq-bt-section__title">Format details</div>
                  <div class="row g-3">
                    <div class="col-md-6">
                      <label class="form-label">Name you’ll recognise</label>
                      <input v-model="form.name" class="form-control" required placeholder="e.g. Equity bulk pay" />
                    </div>
                    <div class="col-md-3">
                      <label class="form-label">File the bank expects</label>
                      <select v-model="form.file_format" class="form-select">
                        <option value="csv">CSV</option>
                        <option value="xlsx">Excel</option>
                      </select>
                    </div>
                    <div class="col-md-3 d-flex align-items-end">
                      <div class="form-check form-switch mb-2">
                        <input
                          id="bt-default"
                          v-model="form.is_default"
                          class="form-check-input"
                          type="checkbox"
                        />
                        <label class="form-check-label" for="bt-default">Use this by default</label>
                      </div>
                    </div>
                    <div class="col-md-6">
                      <label class="form-label">Date to print as the payment date</label>
                      <select v-model="form.layout.payment_date_rule" class="form-select">
                        <option
                          v-for="r in paymentDateRules"
                          :key="r.value"
                          :value="r.value"
                        >
                          {{ paymentDateLabel(r) }}
                        </option>
                      </select>
                      <div class="form-text">Used only by the column choice “Payment date from the setting above”.</div>
                    </div>
                    <div class="col-md-6">
                      <label class="form-label">Only for this bank code <span class="text-muted fw-normal">(optional)</span></label>
                      <input
                        v-model="form.filter_bank_code"
                        class="form-control"
                        placeholder="Leave blank for every bank"
                      />
                      <div class="form-text">If you pay more than one bank, enter the code so this format is suggested automatically.</div>
                    </div>
                  </div>
                </div>

                <div class="dq-bt-section">
                  <div class="dq-bt-section__title">Match the bank’s spreadsheet</div>
                  <p class="small text-muted mb-3">
                    Most banks only need the columns below. Open this if the bank sent a wide Excel template with a numbered header row.
                  </p>
                  <div class="row g-3">
                    <div class="col-md-4">
                      <label class="form-label">How many columns the bank file has</label>
                      <input
                        v-model.number="form.layout.sparse_column_count"
                        type="number"
                        min="0"
                        class="form-control"
                        placeholder="0 = only the columns you map"
                      />
                      <div class="form-text">Leave 0 unless the bank template is very wide (for example 355 columns).</div>
                    </div>
                    <div class="col-md-8 d-flex align-items-end">
                      <div class="form-check mb-3">
                        <input
                          id="bt-numbered"
                          v-model="form.layout.use_numbered_header"
                          class="form-check-input"
                          type="checkbox"
                        />
                        <label class="form-check-label" for="bt-numbered">
                          Bank file starts with a row of column numbers (1, 2, 3…)
                        </label>
                      </div>
                    </div>
                    <div class="col-12">
                      <div class="dq-bt-import">
                        <div class="fw-semibold mb-1">Read column names from the bank’s file</div>
                        <p class="small text-muted mb-2">
                          Upload the empty template from the bank. We copy the headings. You still choose what payroll information goes in each column.
                        </p>
                        <div class="row g-2 align-items-end">
                          <div class="col-md-3">
                            <label class="form-label small">Row that has the column titles</label>
                            <input
                              v-model.number="importHeaderRow"
                              type="number"
                              min="1"
                              max="20"
                              class="form-control form-control-sm"
                            />
                          </div>
                          <div class="col-md-3">
                            <label class="form-label small">Row of numbers above the titles</label>
                            <input
                              v-model.number="importNumberRow"
                              type="number"
                              min="0"
                              max="20"
                              class="form-control form-control-sm"
                            />
                            <div class="form-text">Use 0 if there is no number row.</div>
                          </div>
                          <div class="col-md-6">
                            <label class="form-label small">Bank template</label>
                            <input
                              type="file"
                              class="form-control form-control-sm"
                              accept=".xlsx,.xls,.csv"
                              @change="onImportFile"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="dq-bt-section dq-bt-section--table">
                  <div class="d-flex flex-wrap justify-content-between align-items-end gap-2 mb-3">
                    <div>
                      <div class="dq-bt-section__title mb-1">Columns in the bank file</div>
                      <p class="small text-muted mb-0">
                        For each heading the bank uses, choose the payroll information that should appear there.
                      </p>
                    </div>
                    <button type="button" class="btn btn-sm btn-outline-primary" @click="addColumn">
                      <i class="ti ti-plus me-1"></i>Add column
                    </button>
                  </div>

                  <div class="row g-2 align-items-end mb-3">
                    <div class="col-md-4">
                      <label class="form-label small mb-1">Find a column</label>
                      <input
                        v-model="columnSearch"
                        class="form-control form-control-sm"
                        placeholder="Type a heading, e.g. Account"
                      />
                    </div>
                    <div class="col-md-3">
                      <label class="form-label small mb-1">Check against this payroll month</label>
                      <input v-model="previewPeriod" type="month" class="form-control form-control-sm" />
                    </div>
                    <div class="col-md-5 d-flex flex-wrap gap-2 justify-content-md-end align-items-center">
                      <div class="form-check mb-0">
                        <input
                          id="bt-mapped-only"
                          v-model="showMappedOnly"
                          class="form-check-input"
                          type="checkbox"
                        />
                        <label class="form-check-label small" for="bt-mapped-only">Filled columns only</label>
                      </div>
                      <button
                        v-if="selectedId > 0"
                        type="button"
                        class="btn btn-sm btn-outline-secondary"
                        @click="runPreview"
                      >
                        Check for problems
                      </button>
                      <button
                        v-if="selectedId > 0"
                        type="button"
                        class="btn btn-sm btn-outline-secondary"
                        @click="downloadPreview"
                      >
                        Download sample
                      </button>
                    </div>
                  </div>

                  <div v-if="previewWarnings.length" class="alert alert-warning py-2 small mb-2">
                    <div v-for="(w, i) in previewWarnings" :key="i">{{ w.message }}</div>
                  </div>

                  <div class="table-responsive dq-bt-table-wrap">
                    <table class="table table-sm align-middle dq-bt-table mb-0">
                      <thead>
                        <tr>
                          <th class="dq-bt-col-num">Order</th>
                          <th class="dq-bt-col-num">Excel column</th>
                          <th>Heading on the bank file</th>
                          <th>What to put in this column</th>
                          <th>Same text every row</th>
                          <th>How to write it</th>
                          <th class="dq-bt-col-actions"></th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-if="!visibleColumnIndices.length">
                          <td colspan="7" class="text-muted small py-4 text-center">
                            No columns match this view. Clear the search or turn off “Filled columns only”.
                          </td>
                        </tr>
                        <tr v-for="idx in visibleColumnIndices" :key="idx">
                          <td>
                            <input
                              v-model.number="form.columns[idx].sort"
                              type="number"
                              min="1"
                              class="form-control form-control-sm dq-bt-num-input"
                              title="Order in the file"
                            />
                          </td>
                          <td>
                            <input
                              v-model.number="form.columns[idx].column_index"
                              type="number"
                              min="1"
                              class="form-control form-control-sm dq-bt-num-input"
                              title="Column number on the bank spreadsheet"
                            />
                          </td>
                          <td>
                            <input
                              v-model="form.columns[idx].header"
                              class="form-control form-control-sm"
                              required
                              placeholder="As printed by the bank"
                            />
                          </td>
                          <td>
                            <select
                              v-model="form.columns[idx].source"
                              class="form-select form-select-sm dq-bt-source"
                            >
                              <option value="">Leave this column empty</option>
                              <optgroup
                                v-for="group in groupedSources"
                                :key="group.label"
                                :label="group.label"
                              >
                                <option
                                  v-for="s in group.items"
                                  :key="s"
                                  :value="s"
                                >
                                  {{ sourceLabel(s) }}
                                </option>
                              </optgroup>
                            </select>
                            <div
                              v-if="sourceHint(form.columns[idx].source)"
                              class="dq-bt-hint"
                            >
                              {{ sourceHint(form.columns[idx].source) }}
                            </div>
                          </td>
                          <td>
                            <input
                              v-if="form.columns[idx].source === 'constant'"
                              v-model="form.columns[idx].constant"
                              class="form-control form-control-sm"
                              placeholder="e.g. SALA"
                            />
                            <input
                              v-else-if="form.columns[idx].source === 'formula'"
                              v-model="form.columns[idx].formula"
                              class="form-control form-control-sm"
                              placeholder="SALARY {period_label}"
                            />
                            <span v-else class="text-muted small">Not needed</span>
                          </td>
                          <td>
                            <div class="dq-bt-formats">
                              <label class="dq-bt-chip">
                                <input
                                  type="checkbox"
                                  :checked="hasTransform(form.columns[idx], 'digits')"
                                  @change="toggleTransform(form.columns[idx], 'digits', $event.target.checked)"
                                />
                                Numbers only
                              </label>
                              <label class="dq-bt-chip">
                                <input
                                  type="checkbox"
                                  :checked="hasTransform(form.columns[idx], 'pad13')"
                                  @change="toggleTransform(form.columns[idx], 'pad13', $event.target.checked)"
                                />
                                Pad account to 13 digits
                              </label>
                              <label class="dq-bt-chip">
                                <input
                                  type="checkbox"
                                  :checked="hasTransform(form.columns[idx], 'date')"
                                  @change="toggleTransform(form.columns[idx], 'date', $event.target.checked)"
                                />
                                Date as dd/mm/yyyy
                              </label>
                            </div>
                          </td>
                          <td class="text-nowrap">
                            <button
                              type="button"
                              class="btn btn-sm btn-light py-0"
                              title="Move up"
                              :disabled="idx === 0"
                              @click="moveColumn(idx, -1)"
                            >
                              <i class="ti ti-arrow-up"></i>
                            </button>
                            <button
                              type="button"
                              class="btn btn-sm btn-light py-0"
                              title="Move down"
                              :disabled="idx >= form.columns.length - 1"
                              @click="moveColumn(idx, 1)"
                            >
                              <i class="ti ti-arrow-down"></i>
                            </button>
                            <button
                              type="button"
                              class="btn btn-sm btn-light text-danger py-0"
                              title="Remove column"
                              @click="form.columns.splice(idx, 1)"
                            >
                              <i class="ti ti-trash"></i>
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p class="small text-muted mt-2 mb-3">
                    Formatting is optional. Use <strong>Numbers only</strong> to drop spaces and dashes,
                    <strong>Pad account to 13 digits</strong> when the bank requires a 13-digit account,
                    and <strong>Date as dd/mm/yyyy</strong> on date columns.
                  </p>
                </div>

                <button
                  type="submit"
                  class="btn btn-primary"
                  v-can="'Payroll.Edit'"
                  :disabled="saving"
                >
                  <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
                  {{ selectedId === -1 ? "Create format" : "Save format" }}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div
    class="modal fade dq-confirm-modal"
    id="deleteBankTransferFormatModal"
    tabindex="-1"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered modal-sm">
      <div class="modal-content dq-modal-content">
        <div class="modal-body text-center dq-confirm-body">
          <div class="dq-confirm-icon dq-confirm-red">
            <i class="ti ti-trash-x"></i>
          </div>
          <h4 class="mb-1 dq-confirm-title">Confirm Delete</h4>
          <p class="mb-3 dq-confirm-sub">
            <template v-if="form.name">
              <strong>{{ form.name }}</strong> will be permanently removed.
            </template>
            <template v-else>
              This bank transfer format will be permanently removed.
            </template>
            This action cannot be undone.
          </p>
          <div class="d-flex justify-content-center gap-2">
            <button
              type="button"
              class="btn btn-light dq-btn-muted"
              data-bs-dismiss="modal"
              :disabled="deleting"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-danger dq-btn-danger"
              :disabled="deleting"
              @click="confirmRemoveTemplate"
            >
              <span
                v-if="deleting"
                class="spinner-border spinner-border-sm me-2"
              ></span>
              Yes, Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import moment from "moment";
import { Modal } from "bootstrap";
import layoutHeader from "@/views/layouts/layout-header.vue";
import layoutSidebar from "@/views/layouts/layout-sidebar.vue";
import bankTransferTemplateService from "@/services/bankTransferTemplate.service.js";
import { triggerDownload } from "@/utils/bankTransferFileDownload.js";
import {
  clearBootstrapModalResidue,
  hideBootstrapModalById,
} from "@/utils/bootstrapModal";
import { toast } from "@/utils/toast";

const title = "Bank Transfer Formats";
const text = "Payroll";
const text1 = "Bank upload columns";

const templates = ref([]);
const sourceOptions = ref([]);
const legacyColumns = ref([]);
const corporateStarter = ref(null);
const paymentDateRules = ref([
  { value: "export_day", label: "Export day (today)" },
  { value: "period_end", label: "Payroll period end" },
  { value: "period_start", label: "Payroll period start" },
  { value: "payroll_payment_date", label: "Stored payment date on row" },
]);
const selectedId = ref(0);
const saving = ref(false);
const deleting = ref(false);
const previewWarnings = ref([]);
const importHeaderRow = ref(2);
const importNumberRow = ref(1);
const showMappedOnly = ref(false);
const columnSearch = ref("");
const previewPeriod = ref(moment().format("YYYY-MM"));

const PAYMENT_DATE_LABELS = {
  export_day: "The day you download the file",
  period_end: "Last day of the payroll period",
  period_start: "First day of the payroll period",
  payroll_payment_date: "Date on the pay row (this is also the download day)",
};

const SOURCE_META = {
  "employee.employee_id": {
    group: "This employee",
    label: "Employee number",
    hint: "The payroll number on the employee record.",
  },
  "employee.employee_name": {
    group: "This employee",
    label: "Name to pay",
    hint: "Account name if one is saved. Otherwise the employee’s full name.",
  },
  "employee.bank_name": {
    group: "This employee",
    label: "Employee’s bank",
    hint: "Bank name on the employee record.",
  },
  "employee.bank_code": {
    group: "This employee",
    label: "Employee’s bank code",
    hint: "Bank code on the employee record.",
  },
  "employee.branch_name": {
    group: "This employee",
    label: "Employee’s branch",
    hint: "Branch on the employee record.",
  },
  "employee.branch_code": {
    group: "This employee",
    label: "Employee’s branch code",
    hint: "Branch code on the employee record.",
  },
  "employee.account_number": {
    group: "This employee",
    label: "Account to credit",
    hint: "Bank account number. The dedicated bank-account field is used when it is filled in.",
  },
  "employee.amount": {
    group: "This employee",
    label: "Net pay",
    hint: "Net pay for this person, to two decimal places.",
  },
  "employee.email": {
    group: "This employee",
    label: "Employee email",
    hint: "Email on the employee record.",
  },
  "employee.currency": {
    group: "Same on every row",
    label: "Currency",
    hint: "Your company currency. KES when none is set.",
  },
  "employee.period": {
    group: "Same on every row",
    label: "Period code",
    hint: "A normal month is 2026-10. An advance run is 2026-10:advance. A final run is 2026-10:final.",
  },
  "employee.period_label": {
    group: "Same on every row",
    label: "Period name",
    hint: "Example: October 2026. Advance and final runs add (Advance) or (Final).",
  },
  "employee.reference": {
    group: "Same on every row",
    label: "Salary reference",
    hint: "Always “SALARY” plus the period name. Example: SALARY October 2026.",
  },
  "employee.payment_date": {
    group: "Same on every row",
    label: "Download date",
    hint: "Today’s date, as year-month-day. This does not use the payment-date setting above.",
  },
  "company.company_name": {
    group: "Your company",
    label: "Company name",
    hint: "From Company details.",
  },
  "company.account_number": {
    group: "Your company",
    label: "Company account to debit",
    hint: "The account money is taken from.",
  },
  "company.account_holder_name": {
    group: "Your company",
    label: "Company account name",
    hint: "Name on the company bank account.",
  },
  "company.bank_name": {
    group: "Your company",
    label: "Company bank",
    hint: "From Company details.",
  },
  "company.bank_code": {
    group: "Your company",
    label: "Company bank code",
    hint: "From Company details.",
  },
  "company.branch_name": {
    group: "Your company",
    label: "Company branch",
    hint: "From Company details.",
  },
  "company.branch_code": {
    group: "Your company",
    label: "Company branch code",
    hint: "From Company details.",
  },
  "period.period": {
    group: "Same on every row",
    label: "Period code (same result)",
    hint: "Writes the same code as Period code. A normal month is 2026-10.",
  },
  "period.period_label": {
    group: "Same on every row",
    label: "Period name (same result)",
    hint: "Writes the same name as Period name. Example: October 2026.",
  },
  "period.frequency": {
    group: "Same on every row",
    label: "Pay frequency",
    hint: "monthly, weekly, fortnightly, or daily.",
  },
  "run.payment_date": {
    group: "Same on every row",
    label: "Payment date from the setting above",
    hint: "Uses “Date to print as the payment date”. Written as year-month-day unless you also choose Date as dd/mm/yyyy.",
  },
  constant: {
    group: "Type it yourself",
    label: "The same text on every row",
    hint: "Type the exact words in the next box. Example: SALA.",
  },
  formula: {
    group: "Type it yourself",
    label: "Text with placeholders",
    hint: "Type the words in the next box. You can insert {period_label}, {period}, {employee_name}, {employee_id}, or {reference}.",
  },
};

const form = reactive({
  name: "",
  file_format: "csv",
  is_default: false,
  filter_bank_code: "",
  layout: {
    sparse_column_count: 0,
    use_numbered_header: false,
    sheet_title: "Bank upload",
    payment_date_rule: "export_day",
  },
  columns: [],
});

const editorTitle = computed(() =>
  selectedId.value === 0 ? "Standard (DoneQuick)" : form.name || "Edit format",
);

const editorSubtitle = computed(() => {
  if (selectedId.value === 0) return "Built-in file. Nothing to configure.";
  if (selectedId.value === -1) return "New format. Save it when the columns look right.";
  return "Changes apply the next time you export this format.";
});

const groupedSources = computed(() => {
  const order = ["This employee", "Your company", "Same on every row", "Type it yourself", "Other"];
  const buckets = Object.fromEntries(order.map((label) => [label, []]));
  for (const key of sourceOptions.value) {
    const group = SOURCE_META[key]?.group || "Other";
    (buckets[group] || buckets.Other).push(key);
  }
  return order
    .filter((label) => buckets[label].length)
    .map((label) => ({ label, items: buckets[label] }));
});

const visibleColumnIndices = computed(() => {
  const query = columnSearch.value.trim().toLowerCase();
  return form.columns
    .map((_, i) => i)
    .filter((i) => {
      const col = form.columns[i];
      if (showMappedOnly.value && !(col?.source || "").trim()) return false;
      if (!query) return true;
      const header = String(col?.header || "").toLowerCase();
      const label = sourceLabel(col?.source || "").toLowerCase();
      return header.includes(query) || label.includes(query);
    });
});

function sourceLabel(key) {
  if (!key) return "";
  return SOURCE_META[key]?.label || key;
}

function sourceHint(key) {
  if (!key) return "";
  return SOURCE_META[key]?.hint || "";
}

function paymentDateLabel(rule) {
  return PAYMENT_DATE_LABELS[rule?.value] || rule?.label || "";
}

function hasTransform(col, kind) {
  const text = String(col?.transformsText || "").toLowerCase();
  if (kind === "digits") return text.includes("digits");
  if (kind === "pad13") return text.includes("pad13");
  if (kind === "date") return text.includes("dd/mm/yyyy");
  return false;
}

function toggleTransform(col, kind, on) {
  const parts = [];
  const digits = kind === "digits" ? on : hasTransform(col, "digits");
  const pad13 = kind === "pad13" ? on : hasTransform(col, "pad13");
  const date = kind === "date" ? on : hasTransform(col, "date");
  if (digits) parts.push("digits");
  if (pad13) parts.push("pad13");
  if (date) parts.push("dd/MM/yyyy");
  col.transformsText = parts.join(" ");
  applyTransformShortcut(col);
}

function mappedColumnCount(t) {
  return (t.columns || []).filter((c) => (c.source || "").trim() !== "").length;
}

function previewParams() {
  return {
    frequency: "monthly",
    period: previewPeriod.value,
    limit: 10,
  };
}

function moveColumn(index, direction) {
  const target = index + direction;
  if (target < 0 || target >= form.columns.length) return;
  const cols = form.columns;
  [cols[index], cols[target]] = [cols[target], cols[index]];
  cols.forEach((c, i) => {
    c.sort = i + 1;
  });
}

function cloneColumns(cols) {
  return (cols || []).map((c, i) => ({
    sort: c.sort ?? i + 1,
    column_index: c.column_index ?? c.sort ?? i + 1,
    header: c.header || "",
    source: c.source ?? "",
    constant: c.constant ?? "",
    formula: c.formula ?? "",
    transforms: Array.isArray(c.transforms) ? [...c.transforms] : [],
    transformsText: transformsToText(c.transforms),
  }));
}

function layoutFromApi(layout) {
  const l = layout || {};
  const headerRows = l.header_rows || [];
  return {
    sparse_column_count: l.sparse_column_count ?? 0,
    use_numbered_header: headerRows.some((r) => r.type === "column_numbers"),
    sheet_title: l.sheet_title || "Bank upload",
    payment_date_rule: l.payment_date_rule || "export_day",
  };
}

function layoutToPayload() {
  const header_rows = [];
  if (form.layout.use_numbered_header) {
    header_rows.push({ type: "column_numbers", start: 1 });
  }
  header_rows.push({ type: "labels" });
  return {
    sparse_column_count: form.layout.sparse_column_count || 0,
    sheet_title: form.layout.sheet_title || "Bank upload",
    payment_date_rule: form.layout.payment_date_rule || "export_day",
    header_rows,
  };
}

function transformsToText(transforms) {
  if (!Array.isArray(transforms) || !transforms.length) return "";
  const parts = [];
  for (const t of transforms) {
    if (t.type === "pad_left" && t.length === 13) parts.push("pad13");
    else if (t.type === "digits_only") parts.push("digits");
    else if (t.type === "date_format" && t.output === "dd/MM/yyyy") parts.push("dd/MM/yyyy");
  }
  return parts.join(" ");
}

function applyTransformShortcut(col) {
  const text = (col.transformsText || "").toLowerCase();
  const kept = (Array.isArray(col.transforms) ? col.transforms : []).filter(
    (t) =>
      !(
        t?.type === "digits_only" ||
        (t?.type === "pad_left" && t.length === 13) ||
        (t?.type === "date_format" && t.output === "dd/MM/yyyy")
      ),
  );
  const transforms = [...kept];
  if (text.includes("digits")) transforms.push({ type: "digits_only" });
  if (text.includes("pad13")) {
    transforms.push({ type: "pad_left", length: 13, char: "0" });
  }
  if (text.includes("dd/mm/yyyy")) {
    transforms.push({ type: "date_format", output: "dd/MM/yyyy" });
  }
  col.transforms = transforms;
}

function selectLegacy() {
  selectedId.value = 0;
}

function selectTemplate(t) {
  selectedId.value = t.id;
  form.name = t.name;
  form.file_format = t.file_format || "csv";
  form.is_default = !!t.is_default;
  form.filter_bank_code = t.filter_bank_code || "";
  Object.assign(form.layout, layoutFromApi(t.layout));
  form.columns = cloneColumns(t.columns);
  previewWarnings.value = [];
  columnSearch.value = "";
}

function startCreate() {
  selectedId.value = -1;
  form.name = "My bank upload";
  form.file_format = "xlsx";
  form.is_default = false;
  form.layout.sparse_column_count = 0;
  form.layout.use_numbered_header = false;
  form.columns = cloneColumns(legacyColumns.value);
  previewWarnings.value = [];
}

function startCorporateStarter() {
  if (!corporateStarter.value) return;
  selectedId.value = -1;
  const s = corporateStarter.value;
  form.name = s.name;
  form.file_format = s.file_format || "xlsx";
  form.is_default = false;
  Object.assign(form.layout, layoutFromApi(s.layout));
  form.columns = cloneColumns(s.columns);
  previewWarnings.value = [];
}

function addColumn() {
  const n = form.columns.length + 1;
  form.columns.push({
    sort: n,
    column_index: n,
    header: "New column",
    source: "employee.employee_name",
    constant: "",
    transforms: [],
    transformsText: "",
  });
}

async function onImportFile(event) {
  const file = event.target.files?.[0];
  event.target.value = "";
  if (!file) return;
  try {
    const res = await bankTransferTemplateService.importHeaders(file, {
      headerRow: importHeaderRow.value,
      numberRow: importNumberRow.value,
    });
    const imp = res.data?.import;
    const suggested = res.data?.suggested_layout;
    if (suggested) {
      Object.assign(form.layout, layoutFromApi(suggested));
    }
    if (imp?.columns?.length) {
      selectedId.value = selectedId.value || -1;
      form.file_format = "xlsx";
      form.columns = cloneColumns(imp.columns);
      showMappedOnly.value = true;
      toast(`Imported ${imp.columns.length} headers — map sources, then hide unmapped if you like.`, "success");
    }
  } catch (e) {
    toast(e?.response?.data?.message || "Could not read bank file.", "error");
  }
}

async function downloadPreview() {
  if (selectedId.value <= 0) return;
  try {
    const fmt = form.file_format === "xlsx" ? "xlsx" : "csv";
    const res = await bankTransferTemplateService.previewExport(selectedId.value, {
      ...previewParams(),
      format: fmt,
    });
    const ext = fmt === "xlsx" ? "xlsx" : "csv";
    const blob = new Blob([res.data], {
      type:
        fmt === "xlsx"
          ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          : "text/csv;charset=utf-8",
    });
    triggerDownload(blob, `bank-format-preview.${ext}`);
    toast("Sample file downloaded.", "success");
  } catch (e) {
    toast(e?.response?.data?.message || "Could not download sample.", "error");
  }
}

async function runPreview() {
  if (selectedId.value <= 0) return;
  try {
    const res = await bankTransferTemplateService.preview(
      selectedId.value,
      previewParams(),
    );
    previewWarnings.value = res.data?.warnings || [];
    if (!previewWarnings.value.length) {
      toast("Preview OK — no validation issues on sample rows.", "success");
    }
  } catch (e) {
    toast(e?.response?.data?.message || "Preview failed.", "error");
  }
}

async function load() {
  const [metaRes, listRes] = await Promise.all([
    bankTransferTemplateService.meta(),
    bankTransferTemplateService.list(),
  ]);
  sourceOptions.value = metaRes.data?.sources || [];
  legacyColumns.value = metaRes.data?.legacy?.columns || [];
  corporateStarter.value = metaRes.data?.starters?.corporate_pay || null;
  if (metaRes.data?.payment_date_rules?.length) {
    paymentDateRules.value = metaRes.data.payment_date_rules;
  }
  templates.value = (listRes.data?.templates || []).filter((t) => !t.system && t.id);
}

async function save() {
  if (selectedId.value === 0) return;
  saving.value = true;
  try {
    form.columns.forEach((c) => applyTransformShortcut(c));
    const payload = {
      name: form.name,
      file_format: form.file_format,
      is_default: form.is_default,
      filter_bank_code: form.filter_bank_code?.trim() || null,
      layout: layoutToPayload(),
      columns: form.columns.map(({ transformsText, ...rest }) => ({
        ...rest,
        constant: rest.source === "constant" ? rest.constant : null,
        formula: rest.source === "formula" ? rest.formula : null,
      })),
    };

    if (selectedId.value === -1) {
      const res = await bankTransferTemplateService.create(payload);
      await load();
      selectTemplate(res.data.template);
      toast("Bank format created.", "success");
    } else {
      const res = await bankTransferTemplateService.update(selectedId.value, payload);
      await load();
      selectTemplate(res.data.template);
      toast("Bank format saved.", "success");
    }
  } catch (e) {
    toast(e?.response?.data?.message || "Could not save bank format.", "error");
  } finally {
    saving.value = false;
  }
}

async function duplicateTemplate() {
  if (selectedId.value <= 0) return;
  try {
    const res = await bankTransferTemplateService.duplicate(selectedId.value);
    await load();
    selectTemplate(res.data.template);
    toast("Format duplicated.", "success");
  } catch (e) {
    toast(e?.response?.data?.message || "Could not duplicate.", "error");
  }
}

function openRemoveTemplateModal() {
  if (selectedId.value <= 0) return;
  const el = document.getElementById("deleteBankTransferFormatModal");
  if (el) {
    Modal.getOrCreateInstance(el).show();
  }
}

async function confirmRemoveTemplate() {
  if (selectedId.value <= 0 || deleting.value) return;
  deleting.value = true;
  try {
    await bankTransferTemplateService.remove(selectedId.value);
    await hideBootstrapModalById("deleteBankTransferFormatModal");
    clearBootstrapModalResidue();
    await load();
    selectLegacy();
    toast("Format deleted.", "success");
  } catch (e) {
    toast(e?.response?.data?.message || "Could not delete.", "error");
  } finally {
    deleting.value = false;
  }
}

onMounted(() => {
  const deleteEl = document.getElementById("deleteBankTransferFormatModal");
  deleteEl?.addEventListener("hidden.bs.modal", clearBootstrapModalResidue);

  load().catch((e) => {
    console.error("Bank transfer formats load failed:", e);
    toast(
      e?.response?.data?.message || "Could not load bank formats.",
      "error",
    );
  });
});
</script>

<style scoped>
.dq-bt-lead {
  max-width: 52rem;
  margin-bottom: 1rem;
  color: var(--bs-secondary-color, #5c6570);
  line-height: 1.55;
}
.dq-bt-card {
  border: 1px solid var(--bs-border-color, #e6e8ec);
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
}
.dq-bt-card__head {
  background: #f8fafc;
  border-bottom: 1px solid var(--bs-border-color, #e6e8ec);
}
.dq-bt-card__sub {
  margin-top: 0.15rem;
  font-size: 0.8125rem;
  color: var(--bs-secondary-color, #5c6570);
}
.dq-bt-badge {
  background: #eef2ff;
  color: #3730a3;
  font-weight: 600;
}
.dq-template-item {
  cursor: pointer;
  padding-top: 0.85rem;
  padding-bottom: 0.85rem;
}
.dq-template-item.active {
  background: #f4f7ff;
  color: var(--bs-body-color, #212529);
  box-shadow: inset 3px 0 0 #3554d1;
}
.dq-template-item.active .dq-template-item__title {
  color: var(--bs-emphasis-color, #212529);
}
.dq-template-item__meta {
  color: var(--bs-secondary-color, #5c6570);
  margin-top: 0.15rem;
}
.dq-template-item.active .dq-template-item__meta {
  color: var(--bs-secondary-color, #5c6570);
}
.dq-standard-desc {
  color: var(--bs-body-color, #212529);
  line-height: 1.6;
}
.dq-bt-standard {
  padding: 0.25rem 0.15rem 0.5rem;
}
.dq-bt-section {
  border: 1px solid #e7ebf0;
  border-radius: 0.75rem;
  padding: 1rem 1rem 0.25rem;
  margin-bottom: 1rem;
  background: #fff;
}
.dq-bt-section--table {
  padding-bottom: 0.75rem;
}
.dq-bt-section__title {
  font-size: 0.95rem;
  font-weight: 650;
  margin-bottom: 0.75rem;
  color: #1f2937;
}
.dq-bt-import {
  border: 1px dashed #d5dbe3;
  border-radius: 0.65rem;
  padding: 0.85rem 0.9rem 0.7rem;
  background: #f8fafc;
}
.dq-bt-table-wrap {
  border: 1px solid #e7ebf0;
  border-radius: 0.65rem;
}
.dq-bt-table thead th {
  background: #f8fafc;
  font-size: 0.75rem;
  font-weight: 650;
  letter-spacing: 0.01em;
  color: #475569;
  white-space: nowrap;
  vertical-align: bottom;
}
.dq-bt-table tbody td {
  vertical-align: top;
  padding-top: 0.65rem;
  padding-bottom: 0.65rem;
}
.dq-bt-source {
  min-width: 14rem;
}
.dq-bt-hint {
  margin-top: 0.3rem;
  font-size: 0.75rem;
  line-height: 1.35;
  color: #64748b;
}
.dq-bt-formats {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 12.5rem;
}
.dq-bt-chip {
  display: flex;
  align-items: flex-start;
  gap: 0.4rem;
  margin: 0;
  padding: 0.28rem 0.45rem;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  background: #fff;
  font-size: 0.75rem;
  line-height: 1.25;
  color: #334155;
  cursor: pointer;
}
.dq-bt-chip input {
  margin-top: 0.1rem;
}
.dq-bt-col-num {
  width: 4.5rem;
  min-width: 4.5rem;
}
.dq-bt-col-actions {
  width: 6.5rem;
}
.dq-bt-num-input {
  min-width: 3.25rem;
  width: 100%;
  padding-left: 0.35rem;
  padding-right: 0.35rem;
  color: var(--bs-emphasis-color, #212529);
  background-color: var(--bs-body-bg, #fff);
  font-weight: 600;
  text-align: center;
  -moz-appearance: textfield;
}
.dq-bt-num-input::-webkit-outer-spin-button,
.dq-bt-num-input::-webkit-inner-spin-button {
  opacity: 1;
  margin: 0;
}
</style>
